import React, { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import { Activity, AlertCircle, CheckCircle2, Coins, Users } from 'lucide-react';
import { Button } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { formatDateTime } from '../../config/appConfig';
import api from '../../services/api';
import type {
  AiUsageEventsResponse,
  AiUsageSummaryResponse,
  EmbeddedInConfigurationHub,
} from './models';
import {
  AiUsageFeatureCard,
  AiUsageHeroChart,
  AiUsageSidebarMetric,
  type AiUsageChartMetric,
} from './AiUsageCharts';
import AiUsageFilterBar from './AiUsageFilterBar';

type PeriodDays = 7 | 30 | 90;

const PERIOD_OPTIONS: Array<{ value: string; label: string; days: PeriodDays }> = [
  { value: '7', label: 'Last 7 days', days: 7 },
  { value: '30', label: 'Last 30 days', days: 30 },
  { value: '90', label: 'Last 90 days', days: 90 },
];

const KNOWN_PROVIDERS = ['openai', 'gemini', 'ollama', 'openai_compatible'] as const;

const FEATURE_LABELS: Record<string, string> = {
  email_prompt: 'Email quick prompt',
  email_chat: 'Email AI chat',
  ai_chat: 'AI Chat',
  client_brief: 'Client pre-call brief',
  next_actions: 'Dashboard next actions',
  smart_reply: 'Smart replies',
  draft_from_email: 'Draft todo / appointment',
  unspecified: 'Unspecified',
};

const PROVIDER_LABELS: Record<string, string> = {
  ollama: 'Ollama',
  openai: 'OpenAI',
  openai_compatible: 'OpenAI-compatible',
  gemini: 'Gemini',
};

function formatProvider(id: string): string {
  return PROVIDER_LABELS[id] ?? id;
}

function formatFeature(id: string): string {
  return FEATURE_LABELS[id] ?? id.replace(/_/g, ' ');
}

function formatCount(n: number): string {
  return n.toLocaleString();
}

function formatUsd(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  if (value === 0) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

function formatUserLabel(
  userId: number,
  userEmail: string | null,
  userDisplayName: string | null,
): string {
  if (userDisplayName?.trim()) return `${userDisplayName.trim()} (${userEmail ?? userId})`;
  if (userEmail) return userEmail;
  return `User #${userId}`;
}

function periodRange(days: PeriodDays): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - days);
  return { from: from.toISOString(), to: to.toISOString() };
}

interface UsageFilters {
  userId: number | null;
  provider: string | null;
  feature: string | null;
}

function buildQueryParams(
  from: string,
  to: string,
  filters: UsageFilters,
  extra?: Record<string, string>,
): string {
  const params = new URLSearchParams({ from, to, ...extra });
  if (filters.userId != null) params.set('userId', String(filters.userId));
  if (filters.provider) params.set('provider', filters.provider);
  if (filters.feature) params.set('feature', filters.feature);
  return params.toString();
}

const StatCard: React.FC<{
  label: string;
  value: string;
  hint?: string;
  icon: React.ReactNode;
  accent?: string;
}> = ({ label, value, hint, icon, accent }) => {
  const { colors } = useTheme();
  return (
    <div className={cn('rounded-xl border p-4', colors.border, colors.surface)}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>{label}</p>
          <p className={cn('mt-1 text-2xl font-semibold tabular-nums', colors.text)}>{value}</p>
          {hint ? <p className={cn('mt-1 text-xs', colors.textMuted)}>{hint}</p> : null}
        </div>
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
            accent ?? 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
          )}
        >
          {icon}
        </div>
      </div>
    </div>
  );
};

const AiUsageDashboard: React.FC<EmbeddedInConfigurationHub> = () => {
  const { colors } = useTheme();
  const [periodDays, setPeriodDays] = useState<PeriodDays>(30);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
  const [chartMetric, setChartMetric] = useState<AiUsageChartMetric>('cost');
  const [exporting, setExporting] = useState(false);
  const range = useMemo(() => periodRange(periodDays), [periodDays]);

  const filters: UsageFilters = useMemo(
    () => ({
      userId: selectedUserId,
      provider: selectedProvider,
      feature: selectedFeature,
    }),
    [selectedUserId, selectedProvider, selectedFeature],
  );

  const metaFilters: UsageFilters = useMemo(
    () => ({
      userId: selectedUserId,
      provider: null,
      feature: selectedFeature,
    }),
    [selectedUserId, selectedFeature],
  );

  const queryString = useMemo(
    () => buildQueryParams(range.from, range.to, filters),
    [range.from, range.to, filters],
  );

  const metaQueryString = useMemo(
    () => buildQueryParams(range.from, range.to, metaFilters),
    [range.from, range.to, metaFilters],
  );

  const {
    data: summary,
    error: summaryError,
    isLoading: summaryLoading,
    mutate: refreshSummary,
  } = useSWR<AiUsageSummaryResponse>(
    `/settings/ai-usage/summary?${queryString}`,
    async (url: string) => (await api.get<AiUsageSummaryResponse>(url)).data,
  );

  const { data: metaSummary } = useSWR<AiUsageSummaryResponse>(
    `/settings/ai-usage/summary?${metaQueryString}`,
    async (url: string) => (await api.get<AiUsageSummaryResponse>(url)).data,
  );

  const {
    data: eventsData,
    error: eventsError,
    isLoading: eventsLoading,
    mutate: refreshEvents,
  } = useSWR<AiUsageEventsResponse>(
    `/settings/ai-usage/events?${buildQueryParams(range.from, range.to, filters, { limit: '25' })}`,
    async (url: string) => (await api.get<AiUsageEventsResponse>(url)).data,
  );

  const providerOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of metaSummary?.byProvider ?? []) {
      counts.set(row.provider, row.requests);
    }
    const ids = new Set<string>([...KNOWN_PROVIDERS, ...Array.from(counts.keys())]);
    return Array.from(ids)
      .map((id) => ({
        id,
        label: formatProvider(id),
        requests: counts.get(id) ?? 0,
      }))
      .sort((a, b) => b.requests - a.requests || a.label.localeCompare(b.label));
  }, [metaSummary?.byProvider]);

  const featureOptions = useMemo(() => {
    const fromData = (metaSummary?.byFeature ?? summary?.byFeature ?? []).map((row) => ({
      value: row.feature,
      label: formatFeature(row.feature),
    }));
    const seen = new Set<string>();
    const options = [{ value: '', label: 'All features' }];
    for (const opt of fromData) {
      if (seen.has(opt.value)) continue;
      seen.add(opt.value);
      options.push(opt);
    }
    for (const [key, label] of Object.entries(FEATURE_LABELS)) {
      if (!seen.has(key)) options.push({ value: key, label });
    }
    return options;
  }, [metaSummary?.byFeature, summary?.byFeature]);

  const userOptions = useMemo(() => {
    const rows = metaSummary?.byUser ?? summary?.byUser ?? [];
    return [
      { value: '', label: 'All users' },
      ...rows.map((u) => ({
        value: String(u.userId),
        label: formatUserLabel(u.userId, u.userEmail, u.userDisplayName),
      })),
    ];
  }, [metaSummary?.byUser, summary?.byUser]);

  const refresh = () => {
    void refreshSummary();
    void refreshEvents();
  };

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await api.get(`/settings/ai-usage/export?${queryString}`, {
        responseType: 'blob',
      });
      const stamp = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(res.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `ai-usage-${stamp}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [queryString]);

  const thClass = cn('px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide', colors.textMuted);
  const tdClass = cn('px-3 py-2 text-sm', colors.text);
  const rowBorder = cn('border-t', colors.border);

  const chartDays = summary?.byDay ?? [];
  const heroMetricLabel =
    chartMetric === 'cost' ? 'Total spend' : chartMetric === 'tokens' ? 'Total tokens' : 'Total requests';
  const heroMetricValue =
    chartMetric === 'cost'
      ? formatUsd(summary?.totals.estimatedCostUsd)
      : chartMetric === 'tokens'
        ? formatCount(summary?.totals.totalTokens ?? 0)
        : formatCount(summary?.totals.requests ?? 0);

  const hasActiveFilters = selectedProvider != null || selectedFeature != null || selectedUserId != null;

  const clearAllFilters = () => {
    setSelectedProvider(null);
    setSelectedFeature(null);
    setSelectedUserId(null);
  };

  return (
    <div className="space-y-5">
      <AiUsageFilterBar
        periodDays={periodDays}
        periodOptions={PERIOD_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        onPeriodChange={(days) => setPeriodDays(days as PeriodDays)}
        chartMetric={chartMetric}
        onChartMetricChange={setChartMetric}
        selectedProvider={selectedProvider}
        onProviderChange={setSelectedProvider}
        providerOptions={providerOptions}
        selectedFeature={selectedFeature}
        onFeatureChange={setSelectedFeature}
        featureOptions={featureOptions}
        selectedUserId={selectedUserId}
        onUserChange={setSelectedUserId}
        userOptions={userOptions}
        onRefresh={refresh}
        onExport={() => void handleExport()}
        exporting={exporting}
      />

      {hasActiveFilters ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('text-xs font-medium', colors.textMuted)}>Active filters:</span>
          {selectedProvider ? (
            <span className={cn('rounded-full border px-2.5 py-0.5 text-xs', colors.border, colors.text)}>
              {formatProvider(selectedProvider)}
            </span>
          ) : null}
          {selectedFeature ? (
            <span className={cn('rounded-full border px-2.5 py-0.5 text-xs', colors.border, colors.text)}>
              {formatFeature(selectedFeature)}
            </span>
          ) : null}
          {selectedUserId != null ? (
            <span className={cn('rounded-full border px-2.5 py-0.5 text-xs', colors.border, colors.text)}>
              {userOptions.find((u) => u.value === String(selectedUserId))?.label ?? `User #${selectedUserId}`}
            </span>
          ) : null}
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={clearAllFilters}>
            Clear all
          </Button>
        </div>
      ) : null}

      {summaryError ? (
        <div className={cn('rounded-xl border px-4 py-3 text-sm', colors.border, colors.error, 'bg-red-500/5')}>
          Could not load usage summary. Run database migrations and ensure you have Configuration settings view permission.
        </div>
      ) : null}

      {summaryLoading && !summary ? (
        <p className={cn('text-sm', colors.textMuted)}>Loading usage summary…</p>
      ) : summary ? (
        <div className="space-y-6">
          <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <AiUsageHeroChart
              days={chartDays}
              metric={chartMetric}
              totalLabel={heroMetricLabel}
              totalValue={heroMetricValue}
            />
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
              <AiUsageSidebarMetric
                label="Total tokens"
                value={formatCount(summary.totals.totalTokens)}
                days={chartDays}
                metric="tokens"
              />
              <AiUsageSidebarMetric
                label="Total requests"
                value={formatCount(summary.totals.requests)}
                days={chartDays}
                metric="requests"
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Success rate"
              value={
                summary.totals.requests > 0
                  ? `${Math.round((summary.totals.successful / summary.totals.requests) * 100)}%`
                  : '—'
              }
              hint={`${formatCount(summary.totals.successful)} ok · ${formatCount(summary.totals.failed)} failed`}
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden />}
              accent="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
            />
            <StatCard
              label="Est. cost"
              value={formatUsd(summary.totals.estimatedCostUsd)}
              hint="Based on per-model pricing in AI Models"
              icon={<Coins className="h-4 w-4" aria-hidden />}
              accent="bg-amber-500/10 text-amber-600 dark:text-amber-400"
            />
            <StatCard
              label="Avg latency"
              value={summary.totals.avgLatencyMs != null ? `${summary.totals.avgLatencyMs} ms` : '—'}
              hint={
                selectedProvider
                  ? `For ${formatProvider(selectedProvider)}`
                  : 'Across all providers in period'
              }
              icon={<Activity className="h-4 w-4" aria-hidden />}
            />
            <StatCard
              label="Active users"
              value={formatCount(summary.byUser.length)}
              hint={
                summary.byUser[0]
                  ? `Top: ${formatUserLabel(summary.byUser[0].userId, summary.byUser[0].userEmail, summary.byUser[0].userDisplayName)}`
                  : 'None yet'
              }
              icon={<Users className="h-4 w-4" aria-hidden />}
              accent="bg-indigo-500/10 text-indigo-600 dark:text-indigo-400"
            />
          </div>

          <section>
            <h3 className={cn('mb-3 text-sm font-semibold', colors.text)}>By feature</h3>
            {summary.byFeature.length === 0 ? (
              <p className={cn('text-sm', colors.textMuted)}>No feature breakdown for the current filters.</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {summary.byFeature.map((row) => (
                  <AiUsageFeatureCard
                    key={row.feature}
                    title={formatFeature(row.feature)}
                    requests={row.requests}
                    totalTokens={row.totalTokens}
                    estimatedCostUsd={row.estimatedCostUsd}
                    days={row.daily ?? []}
                  />
                ))}
              </div>
            )}
          </section>

          {!selectedProvider ? (
            <section>
              <h3 className={cn('mb-2 text-sm font-semibold', colors.text)}>By provider</h3>
              {summary.byProvider.length === 0 ? (
                <p className={cn('text-sm', colors.textMuted)}>No AI usage recorded in this period yet.</p>
              ) : (
                <div className={cn('overflow-x-auto rounded-xl border', colors.border)}>
                  <table className="min-w-full">
                    <thead className={cn(colors.surfaceHover)}>
                      <tr>
                        <th className={thClass}>Provider</th>
                        <th className={thClass}>Requests</th>
                        <th className={thClass}>Failed</th>
                        <th className={thClass}>Tokens</th>
                        <th className={thClass}>Est. cost</th>
                        <th className={thClass}>Avg latency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.byProvider.map((row) => (
                        <tr key={row.provider} className={rowBorder}>
                          <td className={cn(tdClass, 'font-medium')}>
                            <button
                              type="button"
                              onClick={() => setSelectedProvider(row.provider)}
                              className="text-left hover:underline text-violet-600 dark:text-violet-400"
                            >
                              {formatProvider(row.provider)}
                            </button>
                          </td>
                          <td className={cn(tdClass, 'tabular-nums')}>{formatCount(row.requests)}</td>
                          <td className={cn(tdClass, 'tabular-nums', row.failed > 0 && 'text-red-600 dark:text-red-400')}>
                            {formatCount(row.failed)}
                          </td>
                          <td className={cn(tdClass, 'tabular-nums')}>{formatCount(row.totalTokens)}</td>
                          <td className={cn(tdClass, 'tabular-nums font-medium')}>{formatUsd(row.estimatedCostUsd)}</td>
                          <td className={cn(tdClass, 'tabular-nums')}>
                            {row.avgLatencyMs != null ? `${row.avgLatencyMs} ms` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ) : null}

          <section>
            <h3 className={cn('mb-2 text-sm font-semibold', colors.text)}>By user</h3>
            {summary.byUser.length === 0 ? (
              <p className={cn('text-sm', colors.textMuted)}>No user-attributed usage for the current filters.</p>
            ) : (
              <div className={cn('overflow-x-auto rounded-xl border', colors.border)}>
                <table className="min-w-full">
                  <thead className={cn(colors.surfaceHover)}>
                    <tr>
                      <th className={thClass}>User</th>
                      <th className={thClass}>Requests</th>
                      <th className={thClass}>Total tokens</th>
                      <th className={thClass}>Est. cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.byUser.map((row) => (
                      <tr key={row.userId} className={rowBorder}>
                        <td className={cn(tdClass, 'font-medium')}>
                          <button
                            type="button"
                            onClick={() => setSelectedUserId(row.userId)}
                            className="text-left hover:underline text-violet-600 dark:text-violet-400"
                          >
                            {formatUserLabel(row.userId, row.userEmail, row.userDisplayName)}
                          </button>
                        </td>
                        <td className={cn(tdClass, 'tabular-nums')}>{formatCount(row.requests)}</td>
                        <td className={cn(tdClass, 'tabular-nums')}>{formatCount(row.totalTokens)}</td>
                        <td className={cn(tdClass, 'tabular-nums font-medium')}>{formatUsd(row.estimatedCostUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      ) : null}

      <section>
        <h3 className={cn('mb-2 text-sm font-semibold', colors.text)}>Recent requests</h3>
        {eventsError ? (
          <div className={cn('rounded-xl border px-4 py-3 text-sm', colors.border, colors.error, 'bg-red-500/5')}>
            Could not load recent events.
          </div>
        ) : eventsLoading && !eventsData ? (
          <p className={cn('text-sm', colors.textMuted)}>Loading recent events…</p>
        ) : eventsData && eventsData.events.length === 0 ? (
          <p className={cn('text-sm', colors.textMuted)}>No events for the current filters.</p>
        ) : eventsData ? (
          <div className={cn('overflow-x-auto rounded-xl border', colors.border)}>
            <table className="min-w-full">
              <thead className={cn(colors.surfaceHover)}>
                <tr>
                  <th className={thClass}>Time</th>
                  <th className={thClass}>User</th>
                  <th className={thClass}>Provider</th>
                  <th className={thClass}>Model</th>
                  <th className={thClass}>Feature</th>
                  <th className={thClass}>Tokens</th>
                  <th className={thClass}>Cost</th>
                  <th className={thClass}>Status</th>
                </tr>
              </thead>
              <tbody>
                {eventsData.events.map((ev) => (
                  <tr key={ev.id} className={rowBorder}>
                    <td className={cn(tdClass, 'whitespace-nowrap text-xs', colors.textMuted)}>
                      {formatDateTime(new Date(ev.createdAt))}
                    </td>
                    <td className={cn(tdClass, 'max-w-[10rem] truncate text-xs')}>
                      {ev.userId != null
                        ? formatUserLabel(ev.userId, ev.userEmail, null)
                        : '—'}
                    </td>
                    <td className={tdClass}>{formatProvider(ev.provider)}</td>
                    <td className={cn(tdClass, 'max-w-[10rem] truncate font-mono text-xs')}>{ev.modelId}</td>
                    <td className={tdClass}>{formatFeature(ev.feature)}</td>
                    <td className={cn(tdClass, 'tabular-nums')}>
                      {ev.totalTokens != null ? formatCount(ev.totalTokens) : '—'}
                    </td>
                    <td className={cn(tdClass, 'tabular-nums')}>{formatUsd(ev.estimatedCostUsd)}</td>
                    <td className={tdClass}>
                      {ev.success ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                          OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                          <AlertCircle className="h-3.5 w-3.5" aria-hidden />
                          {ev.errorCode ?? 'Failed'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default AiUsageDashboard;
